import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Save, Calendar, Gift, FileText, Info, Loader2, Tag, Users } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { businessPageAccess } from '../../lib/roles';
import { businessApi, errorMessage } from '../../lib/api';
import { flag } from '../../config';
import { trackDealCreate, trackDealEdit } from '../../utils/analytics';
import BusinessHeader from '../../components/business/BusinessHeader';

const DealForm = () => {
  const { dealId } = useParams();
  const { isAuthenticated, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    start_date: '',
    end_date: '',
    generate_lead: false
  });
  const [userBusinesses, setUserBusinesses] = useState([]);
  const [currentBusiness, setCurrentBusiness] = useState(null);

  const isEditing = !!dealId;

  const fetchUserBusinesses = async () => {
    if (!flag('MULTI_BUSINESS')) return;
    try {
      const response = await businessApi.get('/business/my-businesses');
      setUserBusinesses(response.data.businesses || []);
    } catch (error) {
      console.error('Error fetching user businesses:', error);
    }
  };

  const fetchCurrentBusiness = async () => {
    try {
      const response = await businessApi.get('/business/profile');
      setCurrentBusiness(response.data);
    } catch (error) {
      console.error('Error fetching business profile:', error);
    }
  };

  const handleSwitchBusiness = async (businessId) => {
    try {
      await businessApi.post(`/business/switch/${businessId}`, {});
      window.location.reload();
    } catch (error) {
      console.error('Error switching business:', error);
    }
  };

  // Check if user has business access (role logic: lib/roles.js businessPageAccess)
  const hasBusinessAccess = businessPageAccess(user);

  useEffect(() => {
    if (!authLoading && (!isAuthenticated || !hasBusinessAccess)) {
      navigate('/');
    }
  }, [isAuthenticated, authLoading, user, navigate, hasBusinessAccess]);

  useEffect(() => {
    if (isAuthenticated && hasBusinessAccess) {
      fetchUserBusinesses();
      fetchCurrentBusiness();
    }
  }, [isAuthenticated, user, hasBusinessAccess]);

  useEffect(() => {
    if (isEditing && isAuthenticated) {
      fetchDeal();
    }
  }, [dealId, isAuthenticated]);

  const fetchDeal = async () => {
    try {
      const response = await businessApi.get('/business/deals');
      const deal = response.data.find(d => d.id === dealId);
      if (deal) {
        setFormData({
          title: deal.title,
          description: deal.description,
          start_date: deal.start_date ? deal.start_date.split('T')[0] : '',
          end_date: deal.end_date ? deal.end_date.split('T')[0] : '',
          generate_lead: deal.generate_lead || false
        });
      }
    } catch (error) {
      console.error('Error fetching deal:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // The backend has no date-range check, so catch it before the round trip.
    if (formData.start_date && formData.end_date && formData.end_date < formData.start_date) {
      setError('The end date cannot be before the start date.');
      return;
    }

    setLoading(true);

    try {
      const payload = {
        title: formData.title,
        description: formData.description,
        start_date: formData.start_date ? new Date(formData.start_date).toISOString() : null,
        end_date: formData.end_date ? new Date(formData.end_date).toISOString() : null,
        generate_lead: formData.generate_lead
      };

      if (isEditing) {
        await businessApi.put(`/business/deals/${dealId}`, payload);
        trackDealEdit({ dealId });
      } else {
        await businessApi.post('/business/deals', payload);
        trackDealCreate({ dealTitle: formData.title, generateLead: formData.generate_lead });
      }

      navigate('/business/deals');
    } catch (error) {
      console.error('Error saving deal:', error);
      setError(errorMessage(error, 'Failed to save deal. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // V5 Input Focus Handler
  const handleInputFocus = (e) => {
    e.target.style.borderColor = '#8b5cf6';
    e.target.style.boxShadow = '0 0 0 4px rgba(139, 92, 246, 0.1)';
  };

  const handleInputBlur = (e) => {
    e.target.style.borderColor = 'rgba(30, 27, 75, 0.1)';
    e.target.style.boxShadow = 'none';
  };

  // Loading State
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FEF7ED' }}>
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4" style={{ color: '#8b5cf6' }} />
          <p style={{ color: 'rgba(30, 27, 75, 0.5)' }}>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FEF7ED' }}>
      {/* V5 Business Header */}
      <BusinessHeader 
        user={user}
        userName={`${user?.first_name || ''} ${user?.last_name || ''}`}
        currentBusiness={currentBusiness}
        businesses={userBusinesses}
        onSwitchBusiness={handleSwitchBusiness}
      />

      {/* Main Content */}
      <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-8">
        {/* Page Title - V5 Design Language */}
        <div className="mb-8">
          <p className="font-mono text-xs mb-2" style={{ color: '#8b5cf6' }}>
            // {isEditing ? 'edit_deal' : 'new_deal'}
          </p>
          <h1 className="text-[32px] text-[#1e1b4b]" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 500 }}>
            {isEditing ? 'Edit Deal' : 'Create New Deal'}
          </h1>
          <p className="mt-2" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>
            {isEditing ? 'Update your deal details' : 'Set up a new deal for your customers'}
          </p>
        </div>

        {/* Main Card - V5 Design Language */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="bg-white rounded-xl p-5 md:p-8"
          style={{ 
            border: '1px solid rgba(30, 27, 75, 0.05)',
            borderLeft: '4px solid #8b5cf6',
            boxShadow: '0 2px 4px rgba(30, 27, 75, 0.04)'
          }}
        >
          {/* Card Header */}
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'rgba(139, 92, 246, 0.1)' }}>
              <Tag size={24} className="text-[#8b5cf6]" />
            </div>
            <div>
              <h2 className="text-[24px] text-[#1e1b4b]" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 500 }}>
                Deal Details
              </h2>
              <p style={{ color: 'rgba(30, 27, 75, 0.5)' }}>Fill in the information below to {isEditing ? 'update your' : 'create a new'} deal</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Error Message - V5 Colors */}
            {error && (
              <div 
                className="p-4 rounded-xl flex items-center gap-3"
                style={{ 
                  backgroundColor: 'rgba(239, 68, 68, 0.1)',
                  border: '1px solid rgba(239, 68, 68, 0.2)'
                }}
              >
                <svg className="w-5 h-5 flex-shrink-0" style={{ color: '#ef4444' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-[14px] font-medium" style={{ color: '#ef4444' }}>
                  {error}
                  {/limit|upgrade/i.test(error) && (
                    <>
                      {' '}
                      <Link to="/business/subscription-manage" className="underline font-semibold">View plans</Link>
                    </>
                  )}
                </p>
              </div>
            )}

            {/* Deal Title */}
            <div>
              <label htmlFor="deal-title" className="flex items-center gap-2 text-[14px] font-medium mb-2" style={{ color: 'rgba(30, 27, 75, 0.7)' }}>
                <Gift size={18} className="text-[#8b5cf6]" />
                Deal Title <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <input
                type="text"
                id="deal-title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
                placeholder="e.g., Happy Hour Special, Buy One Get One Free"
                required
                className="w-full h-[48px] px-4 text-[16px] rounded-xl transition-all outline-none"
                style={{ border: '2px solid rgba(30, 27, 75, 0.1)' }}
              />
              <p className="text-[13px] mt-2" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>
                A catchy title for your deal
              </p>
            </div>

            {/* Deal Description */}
            <div>
              <label htmlFor="deal-description" className="flex items-center gap-2 text-[14px] font-medium mb-2" style={{ color: 'rgba(30, 27, 75, 0.7)' }}>
                <FileText size={18} className="text-[#8b5cf6]" />
                Deal Description <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <textarea
                id="deal-description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
                placeholder="Describe the deal in detail. What do customers get? Any restrictions?"
                required
                rows={4}
                className="w-full px-4 py-3 text-[16px] rounded-xl transition-all outline-none resize-none"
                style={{ border: '2px solid rgba(30, 27, 75, 0.1)' }}
              />
              <p className="text-[13px] mt-2" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>
                Be specific about what customers receive
              </p>
            </div>

            {/* Divider */}
            <div style={{ borderTop: '1px solid rgba(30, 27, 75, 0.05)', margin: '32px 0' }}></div>

            {/* Scheduling Section */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'rgba(139, 92, 246, 0.1)' }}>
                  <Calendar size={20} className="text-[#8b5cf6]" />
                </div>
                <div>
                  <h3 className="text-[18px] font-semibold text-[#1e1b4b]">Schedule (Optional)</h3>
                  <p className="text-[13px]" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>
                    Set start and end dates for automatic activation
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Start Date */}
                <div>
                  <label htmlFor="deal-start-date" className="block text-[14px] font-medium mb-2" style={{ color: 'rgba(30, 27, 75, 0.7)' }}>
                    Start Date
                  </label>
                  <input
                    type="date"
                    id="deal-start-date"
                name="start_date"
                    value={formData.start_date}
                    onChange={handleChange}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                    className="w-full h-[48px] px-4 text-[16px] rounded-xl transition-all outline-none"
                    style={{ border: '2px solid rgba(30, 27, 75, 0.1)' }}
                  />
                  <p className="text-[13px] mt-2" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>
                    Deal activates on this date
                  </p>
                </div>

                {/* End Date */}
                <div>
                  <label htmlFor="deal-end-date" className="block text-[14px] font-medium mb-2" style={{ color: 'rgba(30, 27, 75, 0.7)' }}>
                    End Date
                  </label>
                  <input
                    type="date"
                    id="deal-end-date"
                name="end_date"
                    value={formData.end_date}
                    onChange={handleChange}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                    className="w-full h-[48px] px-4 text-[16px] rounded-xl transition-all outline-none"
                    style={{ border: '2px solid rgba(30, 27, 75, 0.1)' }}
                  />
                  <p className="text-[13px] mt-2" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>
                    Deal expires on this date
                  </p>
                </div>
              </div>

              {/* Tip Box - V5 Colors (Purple for business tips) */}
              <div 
                className="mt-6 p-4 rounded-xl flex items-start gap-3"
                style={{ 
                  backgroundColor: 'rgba(139, 92, 246, 0.05)',
                  border: '1px solid rgba(139, 92, 246, 0.1)'
                }}
              >
                <Info size={20} className="text-[#8b5cf6] flex-shrink-0 mt-0.5" />
                <p className="text-[14px]" style={{ color: 'rgba(30, 27, 75, 0.7)' }}>
                  <strong className="text-[#8b5cf6]">Tip:</strong> Our system automatically activates and pauses deals based on your schedule. You can also manually pause deals anytime from your dashboard.
                </p>
              </div>
            </div>

            {/* Divider */}
            <div style={{ borderTop: '1px solid rgba(30, 27, 75, 0.05)', margin: '32px 0' }}></div>

            {/* Lead Generation Section */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'rgba(255, 122, 26, 0.1)' }}>
                  <Users size={20} style={{ color: '#FF7A1A' }} />
                </div>
                <div>
                  <h3 className="text-[18px] font-semibold text-[#1e1b4b]">Lead Generation</h3>
                  <p className="text-[13px]" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>
                    Capture customer information when they claim this offer
                  </p>
                </div>
              </div>

              {/* Toggle Switch */}
              <div 
                className="p-5 rounded-xl flex items-center justify-between"
                style={{ 
                  backgroundColor: formData.generate_lead ? 'rgba(255, 122, 26, 0.05)' : 'rgba(30, 27, 75, 0.02)',
                  border: formData.generate_lead ? '2px solid #FF7A1A' : '2px solid rgba(30, 27, 75, 0.1)',
                  transition: 'all 0.2s ease'
                }}
              >
                <div className="flex-1">
                  <p className="font-medium text-[#1e1b4b]">Enable Lead Capture</p>
                  <p className="text-[13px] mt-1" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>
                    Get notified with customer details when someone claims this deal
                  </p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={!!formData.generate_lead}
                  aria-label="Enable lead capture"
                  onClick={() => setFormData(prev => ({ ...prev, generate_lead: !prev.generate_lead }))}
                  className="relative inline-flex h-7 w-12 flex-shrink-0 cursor-pointer rounded-full transition-colors duration-200 ease-in-out focus:outline-none"
                  style={{ 
                    backgroundColor: formData.generate_lead ? '#FF7A1A' : 'rgba(30, 27, 75, 0.2)'
                  }}
                >
                  <span 
                    className="inline-block h-6 w-6 transform rounded-full bg-white shadow-lg transition duration-200 ease-in-out"
                    style={{ 
                      transform: formData.generate_lead ? 'translateX(22px)' : 'translateX(2px)',
                      marginTop: '2px'
                    }}
                  />
                </button>
              </div>

              {/* Info Box when enabled */}
              {formData.generate_lead && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mt-4 p-4 rounded-xl"
                  style={{ 
                    backgroundColor: 'rgba(255, 122, 26, 0.05)',
                    border: '1px solid rgba(255, 122, 26, 0.2)'
                  }}
                >
                  <p className="text-[14px]" style={{ color: 'rgba(30, 27, 75, 0.7)' }}>
                    <strong style={{ color: '#FF7A1A' }}>When enabled:</strong> You&apos;ll receive an email notification with the customer&apos;s name, email, and phone number (if available) each time someone claims this deal. Leads are also saved to your dashboard for follow-up.
                  </p>
                </motion.div>
              )}
            </div>

            {/* Divider */}
            <div style={{ borderTop: '1px solid rgba(30, 27, 75, 0.05)', margin: '32px 0' }}></div>

            {/* Action Buttons - V5 Design Language */}
            <div className="flex gap-4">
              {/* Ghost button for Cancel */}
              <button
                type="button"
                onClick={() => navigate('/business/deals')}
                className="flex-1 py-3 rounded-xl font-semibold transition-all duration-200"
                style={{ color: 'rgba(30, 27, 75, 0.7)' }}
              >
                Cancel
              </button>
              
              {/* Purple primary button for Save */}
              <button
                type="submit"
                disabled={loading}
                className="flex-1 py-3 text-white rounded-xl font-semibold transition-all duration-200 hover:-translate-y-0.5 disabled:opacity-50 flex items-center justify-center gap-2"
                style={{ 
                  background: loading ? 'rgba(30, 27, 75, 0.3)' : 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
                  boxShadow: loading ? 'none' : '0 4px 15px rgba(139, 92, 246, 0.4), 0 0 30px rgba(139, 92, 246, 0.2)'
                }}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save size={18} />
                    {isEditing ? 'Update Deal' : 'Create Deal'}
                  </>
                )}
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default DealForm;
